<!-- ModalEditarSeguimiento.vue — Edición parcial para Brenda -->
<!-- Campos: fecha_compromiso, estatus_almacen, notas -->
<!-- PATCH al endpoint de pedidos -->
<template>
  <div class="modal fade" id="modalEditarSeguimiento" tabindex="-1" ref="el">
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content modal-divisan border-0">
        <!-- Header -->
        <div class="modal-header border-0 pb-2">
          <div class="header-info">
            <div class="header-cliente">
              <span class="badge-cliente">{{ pedidoActual?.cliente_numero }}</span>
              <span class="cliente-nombre">{{ pedidoActual?.cliente }}</span>
            </div>
            <h5 class="header-titulo">Pedido #{{ pedidoActual?.numero_pedido }}</h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :disabled="guardando"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body pt-2 pb-2">
          <!-- Aviso modo solo-notas -->
          <div v-if="soloNotas" class="aviso-solo-notas mb-3">
            <i class="bi bi-lock-fill me-2"></i>
            Pedido entregado — solo puedes modificar las notas.
          </div>

          <!-- Fila: Fecha compromiso + Fecha entrega real (solo si Entregado) -->
          <div v-if="!soloNotas" class="fechas-fila campo-grupo">
            <div>
              <label class="campo-label">
                <i class="bi bi-calendar-event me-1"></i>Fecha compromiso
              </label>
              <input
                v-model="form.fecha_compromiso"
                type="date"
                class="campo-input"
                :class="{ 'campo-input-error': faltaFechaCompromiso }"
                :disabled="guardando || fechaCompromisoFijada"
              />
              <small class="campo-hint">
                <template v-if="fechaCompromisoFijada">
                  <i class="bi bi-lock-fill me-1" style="color: #880e4f"></i>
                  <span style="color: #880e4f">Fecha bloqueada</span>
                </template>
                <template v-else-if="faltaFechaCompromiso">
                  <i class="bi bi-exclamation-triangle-fill me-1" style="color: #c62828"></i>
                  <span style="color: #c62828">Requerida para marcar como entregado</span>
                </template>
                <template v-else>Fecha prometida al cliente</template>
              </small>
            </div>

            <Transition name="fecha-entrega">
              <div v-if="esEntregado">
                <label class="campo-label">
                  <i class="bi bi-calendar-check me-1"></i>Fecha entrega real
                </label>
                <input
                  v-model="form.fecha_entrega_real"
                  type="date"
                  class="campo-input"
                  :disabled="guardando"
                />
                <small class="campo-hint">Día en que se entregó</small>
              </div>
            </Transition>
          </div>

          <!-- Estatus almacén -->
          <div v-if="!soloNotas" class="campo-grupo">
            <label class="campo-label">
              <i class="bi bi-clipboard-check me-1"></i>Estatus almacén
            </label>
            <div class="estatus-opciones">
              <button
                v-for="opcion in ESTATUS_ALMACEN_OPCIONES"
                :key="opcion"
                type="button"
                :class="[
                  'estatus-btn',
                  { active: form.estatus_almacen === opcion },
                  estatusClase(opcion),
                ]"
                @click="seleccionarEstatus(opcion)"
                :disabled="guardando"
              >
                {{ opcion }}
              </button>
            </div>
          </div>

          <!-- Notas -->
          <div class="campo-grupo mb-0">
            <label class="campo-label">
              <i class="bi bi-chat-left-text me-1"></i>Notas
            </label>
            <textarea
              v-model="form.notas"
              class="campo-input campo-textarea"
              placeholder="Observaciones, instrucciones especiales, etc."
              rows="3"
              :disabled="guardando"
            ></textarea>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-cancelar" data-bs-dismiss="modal" :disabled="guardando">
            Cancelar
          </button>
          <button
            class="btn btn-guardar"
            @click="handleGuardar"
            :disabled="guardando || faltaFechaCompromiso"
            :title="
              faltaFechaCompromiso ? 'Captura la fecha compromiso para poder guardar' : ''
            "
          >
            <span v-if="guardando" class="spinner-border spinner-border-sm me-2"></span>
            {{ guardando ? "Guardando..." : "Guardar cambios" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import {
  useEditarSeguimiento,
  ESTATUS_ALMACEN_OPCIONES,
} from "../SeguimientoPedidos/composables/useEditarSeguimiento.js";

const emit = defineEmits(["guardado"]);

const el = ref(null);
let modalInstance = null;

const { pedidoActual, form, guardando, cargar, reset, guardar } = useEditarSeguimiento();

const esEntregado = computed(() => form.value?.estatus_almacen === "Entregado");
const soloNotas = computed(() => pedidoActual.value?.estatus_almacen === "Entregado");

// Si el pedido ya traía fecha_compromiso al abrirse, queda bloqueada para edición
const fechaCompromisoFijada = computed(() => !!pedidoActual.value?.fecha_compromiso);

// Bloquea el guardado: no se marca un pedido como entregado sin fecha compromiso
const faltaFechaCompromiso = computed(
  () => !soloNotas.value && esEntregado.value && !form.value.fecha_compromiso
);

onMounted(() => {
  modalInstance = new Modal(el.value);
});

function abrir(pedido) {
  reset();
  cargar(pedido);
  modalInstance.show();
}
defineExpose({ abrir });

async function handleGuardar() {
  await guardar(() => {
    modalInstance.hide();
    emit("guardado");
  });
}

// Fecha de hoy en zona horaria local ("YYYY-MM-DD").
// toISOString() da UTC y adelantaba un día por la tarde/noche.
function hoyLocal() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

function seleccionarEstatus(opcion) {
  form.value.estatus_almacen = opcion;
  if (opcion === "Entregado") {
    // Precargar con hoy si no tiene fecha real aún
    if (!form.value.fecha_entrega_real) {
      form.value.fecha_entrega_real = hoyLocal();
    }
  } else {
    // Limpiar para no mandar fecha sucia al backend
    form.value.fecha_entrega_real = "";
  }
}

function estatusClase(opcion) {
  return (
    {
      "En proceso": "estatus-en-proceso",
      "Listo para entrega": "estatus-listo",
      Entregado: "estatus-entregado",
      Cancelado: "estatus-cancelado",
    }[opcion] || ""
  );
}
</script>

<style scoped>
/* ── Fila de fechas ─────────────────────────────────────── */
.fechas-fila {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
}

/* Transición suave al aparecer/desaparecer fecha entrega real */
.fecha-entrega-enter-active,
.fecha-entrega-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fecha-entrega-enter-from,
.fecha-entrega-leave-to {
  opacity: 0;
  transform: translateX(6px);
}

/* ── Modal base ─────────────────────────────────────────── */
.modal-divisan {
  background-color: var(--d-cream);
  border: 1.5px solid rgba(64, 34, 8, 0.2);
  border-radius: 18px;
}

/* ── Header ─────────────────────────────────────────────── */
.modal-header {
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.12) !important;
  padding: 1.1rem 1.4rem 0.9rem;
}
.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.header-cliente {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 1px;
}
.badge-cliente {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-family: var(--d-font-mono);
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 7px;
}
.cliente-nombre {
  font-size: 0.82rem;
  color: var(--d-green);
  font-weight: 600;
}
.header-titulo {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--d-brown);
  margin: 0;
}

/* ── Campos ─────────────────────────────────────────────── */
.modal-body {
  padding: 1rem 1.4rem;
}

.aviso-solo-notas {
  background: rgba(46, 125, 50, 0.08);
  border: 1px solid rgba(46, 125, 50, 0.25);
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #1b5e20;
  display: flex;
  align-items: center;
}

.campo-grupo {
  margin-bottom: 1.1rem;
}
.campo-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 6px;
}
.campo-input {
  width: 100%;
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.88rem;
  color: var(--d-brown);
  background: white;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.campo-input:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 3px rgba(40, 54, 24, 0.1);
}
.campo-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.campo-input-error {
  border-color: #c62828;
}
.campo-input-error:focus {
  border-color: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.12);
}
.campo-textarea {
  resize: vertical;
  min-height: 80px;
}
.campo-hint {
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.45);
  margin-top: 3px;
  display: block;
}

/* ── Estatus opciones ───────────────────────────────────── */
.estatus-opciones {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.estatus-btn {
  border: 1.5px solid transparent;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  background: white;
  opacity: 0.55;
}
.estatus-btn:disabled {
  cursor: not-allowed;
}
.estatus-btn.active {
  opacity: 1;
}

/* colores por estatus */
.estatus-en-proceso {
  border-color: #1565c0;
  color: #1565c0;
}
.estatus-en-proceso.active {
  background: #e3f2fd;
}

.estatus-listo {
  border-color: #2e7d32;
  color: #2e7d32;
}
.estatus-listo.active {
  background: #e8f5e9;
}

.estatus-entregado {
  border-color: #1b5e20;
  color: #1b5e20;
}
.estatus-entregado.active {
  background: #e8f5e9;
}

.estatus-cancelado {
  border-color: #880e4f;
  color: #880e4f;
}
.estatus-cancelado.active {
  background: #fce4ec;
}

/* ── Footer ─────────────────────────────────────────────── */
.modal-footer {
  border-top: 0.5px solid rgba(64, 34, 8, 0.12) !important;
  padding: 0.9rem 1.4rem;
}
.btn-cancelar {
  background: none;
  border: 1px solid rgba(96, 108, 56, 0.4);
  color: var(--d-green);
  font-weight: 600;
  border-radius: 8px;
  font-size: 0.85rem;
}
.btn-cancelar:hover:not(:disabled) {
  background: rgba(96, 108, 56, 0.07);
}
.btn-guardar {
  background: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 700;
  border-radius: 8px;
  font-size: 0.85rem;
}
.btn-guardar:hover:not(:disabled) {
  background: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-guardar:disabled,
.btn-cancelar:disabled {
  opacity: 0.45;
}
</style>
